import React, { useRef } from 'react';
import { useInView, useTransform, useReducedMotion } from 'framer-motion';
import { motion } from '../../lib/motionShim';
import { useViewportProgress } from '../../lib/useViewportProgress';
import { useTrioSizes } from '../../lib/useTrioSizes';
import PhoneFrame from '../PhoneFrame';

/**
 * 02 · TRACK — prevention is daily and unglamorous.
 * A trio of devices: hydration front and center, caffeine and body trends
 * flanking in perspective, like a shelf of instruments.
 */
const DailySignals = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-120px' });
  const reduce = useReducedMotion();

  // Depth parallax: the flanking devices travel further than the center one
  const scrollYProgress = useViewportProgress(ref);
  const ySides = useTransform(scrollYProgress, [0, 1], [54, -10]);
  const yCenter = useTransform(scrollYProgress, [0, 1], [16, -4]);
  const sizes = useTrioSizes();

  const rise = (delay) => ({
    initial: { opacity: 0, y: 30 },
    animate: isInView ? { opacity: 1, y: 0 } : {},
    transition: { duration: 0.8, delay, ease: [0.16, 1, 0.3, 1] },
  });

  return (
    <section
      id="signals"
      ref={ref}
      className="relative py-24 md:py-32 bg-ston-bg overflow-hidden"
      data-testid="daily-signals-section"
    >
      <div aria-hidden="true" className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[500px] rounded-full" style={{ background: 'radial-gradient(closest-side, rgba(14,165,233,0.05), rgba(14,165,233,0.025) 55%, transparent 78%)' }} />
      </div>

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-12">
        {/* Copy — centered above the trio */}
        <div className="text-center max-w-[980px] mx-auto">
          <motion.span className="section-label inline-block" {...rise(0)}>
            02 · TRACK
          </motion.span>
          <motion.h2
            className="mt-4 font-outfit font-extrabold italic uppercase leading-[1.02]"
            style={{ fontSize: 'clamp(26px, 3.4vw, 46px)', letterSpacing: '-0.025em' }}
            {...rise(0.1)}
          >
            <span className="block text-ston-text whitespace-nowrap" style={{ fontSize: 'min(1em, 4.9vw)' }}>Sleep, water, caffeine, recovery.</span>
            <span className="block gradient-text">The boring things that decide everything.</span>
          </motion.h2>
          <motion.p
            className="mt-6 mx-auto max-w-[58ch] text-[16.5px] md:text-[17.5px] leading-relaxed text-ston-text-secondary"
            {...rise(0.2)}
          >
            Hydration you actually hit. Caffeine timed so it isn't quietly stealing
            your sleep. Recovery scored each morning. Body measurements trending
            across months, not days — because health moves slowly, and what matters
            is the direction.
          </motion.p>
        </div>

        {/* Device trio — center rises first, wings fan out from behind it */}
        <div
          className="mt-14 md:mt-16 flex items-end justify-center"
          style={{ perspective: 1600 }}
        >
          <motion.div
            className="opacity-90"
            style={reduce ? { rotateY: 16 } : { y: ySides, rotateY: 16 }}
          >
            <motion.div
              initial={reduce ? { opacity: 0 } : { opacity: 0, x: sizes.center * 0.5, scale: 0.92 }}
              whileInView={reduce ? { opacity: 1 } : { opacity: 1, x: 0, scale: 1 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.9, delay: 0.45, ease: [0.16, 1, 0.3, 1] }}
            >
              <PhoneFrame
                src="/screens/caffeine.webp"
                alt="Caffeine tracking — 75 of 400 mg, timing looks okay"
                width={sizes.side}
                glowColor="rgba(251,146,60,0.18)"
              />
            </motion.div>
          </motion.div>
          <motion.div className="relative z-10 -mx-6 md:-mx-8" style={reduce ? {} : { y: yCenter }}>
            <motion.div
              initial={reduce ? { opacity: 0 } : { opacity: 0, y: 44 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.9, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
            >
              <PhoneFrame
                src="/screens/hydration.webp"
                alt="Hydration — 250 ml of a 3000 ml goal, quick add buttons"
                width={sizes.center}
                glowColor="rgba(56,189,248,0.22)"
              />
            </motion.div>
          </motion.div>
          <motion.div
            className="opacity-90"
            style={reduce ? { rotateY: -16 } : { y: ySides, rotateY: -16 }}
          >
            <motion.div
              initial={reduce ? { opacity: 0 } : { opacity: 0, x: -sizes.center * 0.5, scale: 0.92 }}
              whileInView={reduce ? { opacity: 1 } : { opacity: 1, x: 0, scale: 1 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.9, delay: 0.45, ease: [0.16, 1, 0.3, 1] }}
            >
              <PhoneFrame
                src="/screens/nutrition.webp"
                alt="Nutrition tracking — 845 kcal consumed with protein, carbs, fat and fiber progress"
                width={sizes.side}
                glowColor="rgba(168,85,247,0.2)"
              />
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default DailySignals;
